import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';

import { ContractSignerCandidate, ContractSignerParty } from '@/types';

type ContractSignerDialogProps = {
    open: boolean;
    candidates: ContractSignerCandidate[];
    submitting?: boolean;
    onClose: () => void;
    onConfirm: (party: ContractSignerParty) => void;
};

const PARTY_LABEL: Record<ContractSignerParty, string> = {
    contractor: 'Contractor',
    owner: 'Owner',
};

/** Pick which project party signs as client when the estimate has no email. */
export const ContractSignerDialog = ({
    open,
    candidates,
    submitting = false,
    onClose,
    onConfirm,
}: ContractSignerDialogProps) => {
    const selectable = candidates.filter((c) => !!c.email);
    const noneSelectable = selectable.length === 0;
    const [party, setParty] = useState<ContractSignerParty | ''>('');

    // Default to the first party that has an email whenever the dialog (re)opens.
    useEffect(() => {
        if (open) setParty(selectable[0]?.party ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Who will sign the contract?</DialogTitle>
            <DialogContent>
                {noneSelectable ? (
                    <Alert severity="warning">
                        Add an email to the contractor or owner before signing.
                    </Alert>
                ) : (
                    <RadioGroup
                        value={party}
                        onChange={(e) => setParty(e.target.value as ContractSignerParty)}
                    >
                        {candidates.map((c) => (
                            <FormControlLabel
                                key={c.party}
                                value={c.party}
                                control={<Radio />}
                                disabled={!c.email || submitting}
                                label={
                                    <Typography variant="body2">
                                        {PARTY_LABEL[c.party]} — {c.name}
                                        {c.email ? ` (${c.email})` : ' (no email)'}
                                    </Typography>
                                }
                            />
                        ))}
                    </RadioGroup>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={() => party && onConfirm(party)}
                    variant="contained"
                    disabled={submitting || noneSelectable || !party}
                >
                    {submitting ? <CircularProgress size={20} /> : 'Start signing'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
